<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import webapp from '../services/webapp'
import { nextTick, reactive, ref, watch } from 'vue'
import { supportedLanguages, useI18n } from '@/composables/i18n'
import WebApp from '@twa-dev/sdk'
import { usePvpGame } from '@/composables/game/pvp'
import { useScreenSize } from '@/composables/helpers/screen-size'
import { PVP_GAME_STATUS } from '@/types/shared/pvp'
import { useAuth } from '@/composables/auth'
import api from '@/services/api'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { usePlayer } from '@/composables/game/player'
import { Rive, StateMachineInput } from '@rive-app/canvas'
import router from '@/router'
import { RouteName } from '@/routes'
import { MINI_GAME_ID } from '@/types/shared/player-config'

const { profile } = useAuth()

const { changeLanguage, selectedLanguage } = useI18n()
const { updatePlayer } = usePlayer()
const lang = ref(selectedLanguage.value)
const { rawAddress } = useWalletConnect()

watch(lang, lang => {
  changeLanguage(lang)
})

const { matchStatus, isPending, playGame, timeElapsed } = usePvpGame()
watch(matchStatus, matchStatus => {
  if (matchStatus === PVP_GAME_STATUS.PENDING) return
  if (matchStatus === PVP_GAME_STATUS.DRAW) {
    webapp.showAlert(`It's a draw!`)
  } else {
    webapp.showAlert(`You ${matchStatus}!`)
  }
})

const { width, height } = useScreenSize()

function fullScreen() {
  WebApp.requestFullscreen()
}

const balances = ref<Awaited<ReturnType<typeof api.getJettons>>['balances']>([])
watch(rawAddress, async (rawAddress) => {
  if (!rawAddress) return
  const response = await api.getJettons(rawAddress)
  balances.value = response.balances

}, { immediate: true })

async function getFreeStars() {
  const { playerStats } = await api.increaseStars()
  updatePlayer(playerStats)
}

let rivefile: Rive | null = null
let _stateMachiveInputs: StateMachineInput[] = reactive([])
const canvasElement = ref<HTMLCanvasElement | null>(null)

function initTestRive() {
  rivefile = new Rive({
    src: '/rive/pet-abode-baby.riv',
    canvas: canvasElement.value as HTMLCanvasElement,
    autoplay: true,
    stateMachines: 'Baby',
    onLoad: () => {
      if (!rivefile) {
        console.log('issue')
        return
      }
      _stateMachiveInputs = rivefile.stateMachineInputs('Baby')
    },
  })
}

function triggerRiveState(state: string) {
  const input = _stateMachiveInputs.find(i => i.name === state)
  if (!input) return
  const otherTrueInput = _stateMachiveInputs.find(i => i.value === true && i.name !== state)
  if (otherTrueInput) {
    otherTrueInput.value = false
  }
  if (typeof input.value === 'boolean') {
    input.value = !input.value
  } else {
    input.fire()
  }
}

watch(canvasElement, canvasElement => {
  if (!canvasElement) return
  nextTick(() => {
    initTestRive()
  })
})

async function goToMinesweeperGame() {
  try {
    const response = await api.buyMiniGameEntry({ gameId: MINI_GAME_ID.MINESWEEPER })
    console.log('nonce', response.nonce)
    if (!response.nonce) {
      throw new Error(response.error || 'No nonce returned')
    }
    const url = `/${MINI_GAME_ID.MINESWEEPER}?nonce=${response.nonce}`
    window.history.replaceState({}, '', url)
    window.location.reload()
  } catch (error) {
    console.error('Failed to buy mini game entry:', error)
    webapp.showAlert('Failed to enter the game. ' + error)
  }
}
</script>

<template>
  <div>
    <div>
      <div>Your user id: {{ profile.id }}</div>
      <div>Your jettons</div>
      <div v-if="balances.length === 0">
        You have no jettons
      </div>
      <div
        v-for="balance in balances"
        :key="balance.symbol"
      >
        {{ balance.name }}: {{ Number(balance.balance) / Math.pow(10, balance.decimals) }} <img
          :src="balance.image"
          alt="icon"
        >
      </div>
      <div v-if="isPending">
        {{ timeElapsed }} seconds passed
      </div>
    </div>

    <canvas
      ref="canvasElement"
      id="rive_test_canvas"
      style="width: 300px; height: 300px;"
      width="900"
      height="900"
      class="mx-auto"
    />
    <div class="my-4 w-full flex items-center gap-2 flex-wrap">
      <BaseButton @click="triggerRiveState('is hungry')">
        Is hungry
      </BaseButton>
      <BaseButton @click="triggerRiveState('is happy')">
        Is happy
      </BaseButton>
      <BaseButton @click="triggerRiveState('is unhappy')">
        Is unhappy
      </BaseButton>
      <BaseButton @click="triggerRiveState('is sleeping')">
        Is sleeping
      </BaseButton>
      <BaseButton @click="triggerRiveState('is pooping')">
        Is pooping
      </BaseButton>
      <BaseButton @click="triggerRiveState('jump')">
        Jump
      </BaseButton>
    </div>

    <br>
    {{ WebApp.version }}
    {{ webapp.language }}
    <br>
    <br>
    <span class="whitespace-pre-line">
      {{
        `Screen size:
    ${height} h
    ${width} w
    ${webapp.safeAreaInset.top} safe area top
    ${webapp.contentSafeAreaInset.top} content safe area top
    ${webapp.safeAreaInset.bottom} safe area bottom
    ${webapp.contentSafeAreaInset.bottom} content safe area bottom`
      }}
    </span>
    <br>
    <BaseButton @click="goToMinesweeperGame">
      Go to minesweeper
    </BaseButton>
    <br>
    <select
      v-model="lang"
    >
      <option
        v-for="l in supportedLanguages"
        :key="l"
        :value="l"
      >
        {{ l }}
      </option>
    </select>
    <br>

    <BaseButton @click="router.push({name: RouteName.GIVEAWAY})">
      Go to giveaway page
    </BaseButton>
    <BaseButton @click="getFreeStars">
      Get 500 stars
    </BaseButton>
    <BaseButton @click="fullScreen">
      Go full screen
    </BaseButton>
  </div>
</template>

<style scoped>

</style>
